import { configureStore, createListenerMiddleware } from '@reduxjs/toolkit';

import { baseApi } from 'store/baseApi';
import { usersApi } from 'store/entities/users/api';
import { logout, usersSlice } from 'store/entities/users/slice';
import { DIALOG_PROPS_PATH, openDialogAction } from 'store/entities/ui/slice';
import { rtkQueryErrorMiddleware } from 'store/rtkQueryErrorMiddleware';
import { createReduxEnhancer } from 'sentry';

import reducer from './reducers';

const SENTRY_IGNORED_REDUCERS = [baseApi.reducerPath, usersSlice.name];

const sentryReduxEnhancer = createReduxEnhancer({
  stateTransformer: (state) =>
    Object.keys(state).reduce(
      (acc, key) => (SENTRY_IGNORED_REDUCERS.includes(key) ? acc : { ...acc, [key]: state[key] }),
      {},
    ),
  actionTransformer: (action) =>
    SENTRY_IGNORED_REDUCERS.some((reducerName) => action.type.includes(reducerName)) ? null : action,
});

const createLogoutMiddleware = () => {
  const logoutMiddleware = createListenerMiddleware();

  logoutMiddleware.startListening({
    actionCreator: logout,
    effect: (_action, { dispatch }) => {
      dispatch(baseApi.util.resetApiState());
    },
  });

  return logoutMiddleware;
};

const createGetCurrentUserMiddleware = () => {
  const getCurrentUserMiddleware = createListenerMiddleware();

  getCurrentUserMiddleware.startListening({
    matcher: usersApi.endpoints.getCurrentUser.matchFulfilled,
    effect: (action, { dispatch, getState }) => {
      const state = getState();

      if (state.usersSlice.currentUser !== action.payload) {
        dispatch(usersSlice.actions.setCurrentUser(action.payload));
      }
    },
  });

  return getCurrentUserMiddleware;
};

export const configureRootStore = () =>
  configureStore({
    reducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoreActions: [openDialogAction],
          ignoredPaths: [DIALOG_PROPS_PATH],
        },
      }).concat(
        rtkQueryErrorMiddleware,
        createLogoutMiddleware().middleware,
        createGetCurrentUserMiddleware().middleware,
        baseApi.middleware,
      ),
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer),
  });
